import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

// Types
export interface Tab {
  id: string
  label: string
  type: 'home' | 'validation' | 'data' | 'transactions' | 'settings' | 'file' | 'summary' | 'flag' | 'research' | 'notes'
  closable?: boolean
  iconName?: string // Store icon name as string instead of ReactNode
  metadata?: Record<string, any>
}

export interface ResearchMessage {
  id: string
  type: 'user' | 'assistant'
  content: string
  timestamp: Date
  sources?: ResearchSource[]
  saved?: boolean
}

export interface ResearchSource {
  type: 'document' | 'database' | 'web' | 'case'
  title: string
  url?: string
  snippet?: string
}

export interface UIState {
  // Layout state
  layout: {
    sidebarCollapsed: boolean
    sidebarExpandedItems: string[]
    rightSidebarCollapsed: boolean
    activityPanelCollapsed: boolean
    bottomPanelCollapsed: boolean
    bottomPanelTab: 'summary' | 'metrics' | 'verification' | 'validation' | 'notes' | 'custom-calculations'
  }
  
  // Form state
  forms: {
    showCreateCaseForm: boolean
  }
  
  // Tabs state
  tabs: {
    activeTabId: string
    openTabs: Tab[]
  }
  
  // Search state
  search: {
    query: string
    filters: {
      status?: string[]
      type?: string[]
      dateRange?: { start: string; end: string }
    }
  }
  
  // Research state
  research: {
    messages: ResearchMessage[]
    isSearching: boolean
    inputValue: string
  }
  
  // Activity log
  activityLog: string[]
  
  // Toast notifications
  toast: {
    show: boolean
    message: string
    type: 'success' | 'error' | 'info'
  }
}

const initialState: UIState = {
  layout: {
    sidebarCollapsed: false,
    sidebarExpandedItems: ['case-maria-gonzalez-2025', 'documents-folder'],
    rightSidebarCollapsed: true,
    activityPanelCollapsed: false,
    bottomPanelCollapsed: false,
    bottomPanelTab: 'summary'
  },
  forms: {
    showCreateCaseForm: false
  },
  tabs: {
    activeTabId: 'home',
    openTabs: [
      {
        id: 'home',
        label: 'Home',
        type: 'home',
        closable: false,
        iconName: 'Home'
      }
    ]
  },
  search: {
    query: '',
    filters: {}
  },
  research: {
    messages: [
      {
        id: '1',
        type: 'assistant',
        content: 'Hello! I can help you research information for your cases. Ask me about documents, regulations, calculations, or anything else you need to investigate.',
        timestamp: new Date(),
        saved: false
      }
    ],
    isSearching: false,
    inputValue: ''
  },
  activityLog: [
    '[10:23:45] Document uploaded: Maria-Bank-Stmt-Feb-2025.pdf',
    '[10:23:46] Starting Reducto extraction pipeline...',
    '[10:23:48] Extraction completed successfully',
    '[10:23:49] Running validation checks...',
    '[10:23:50] Validation completed with warnings'
  ],
  toast: {
    show: false,
    message: '',
    type: 'success'
  }
}

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    // Layout actions
    setSidebarCollapsed(state, action: PayloadAction<boolean>) {
      state.layout.sidebarCollapsed = action.payload
    },
    toggleSidebar(state) {
      state.layout.sidebarCollapsed = !state.layout.sidebarCollapsed
    },
    setRightSidebarCollapsed(state, action: PayloadAction<boolean>) {
      state.layout.rightSidebarCollapsed = action.payload
    },
    toggleRightSidebar(state) {
      state.layout.rightSidebarCollapsed = !state.layout.rightSidebarCollapsed
    },
    setActivityPanelCollapsed(state, action: PayloadAction<boolean>) {
      state.layout.activityPanelCollapsed = action.payload
    },
    toggleActivityPanel(state) {
      state.layout.activityPanelCollapsed = !state.layout.activityPanelCollapsed
    },
    setBottomPanelCollapsed(state, action: PayloadAction<boolean>) {
      state.layout.bottomPanelCollapsed = action.payload
    },
    toggleBottomPanel(state) {
      state.layout.bottomPanelCollapsed = !state.layout.bottomPanelCollapsed
    },
    setBottomPanelTab(state, action: PayloadAction<'summary' | 'metrics' | 'verification' | 'validation' | 'notes' | 'custom-calculations'>) {
      state.layout.bottomPanelTab = action.payload
    },
    toggleSidebarItem(state, action: PayloadAction<string>) {
      const itemId = action.payload
      const index = state.layout.sidebarExpandedItems.indexOf(itemId)
      if (index > -1) {
        state.layout.sidebarExpandedItems.splice(index, 1)
      } else {
        state.layout.sidebarExpandedItems.push(itemId)
      }
    },
    
    // Auto-hide panels for research mode
    setResearchMode(state, action: PayloadAction<boolean>) {
      if (action.payload) {
        // Hide all panels when entering research mode
        state.layout.sidebarCollapsed = true
        state.layout.activityPanelCollapsed = true
        state.layout.bottomPanelCollapsed = true
      }
    },
    
    // Tab actions
    setActiveTab(state, action: PayloadAction<string>) {
      state.tabs.activeTabId = action.payload
      
      // Auto-hide panels if switching to research tab
      const tab = state.tabs.openTabs.find(t => t.id === action.payload)
      if (tab?.type === 'research') {
        state.layout.sidebarCollapsed = true
        state.layout.activityPanelCollapsed = true
        state.layout.bottomPanelCollapsed = true
      }
    },
    openTab(state, action: PayloadAction<Tab>) {
      const existingTabIndex = state.tabs.openTabs.findIndex(t => t.id === action.payload.id)
      if (existingTabIndex > -1) {
        // Update existing tab with new data (CRITICAL FIX!)
        state.tabs.openTabs[existingTabIndex] = action.payload
        console.log('Redux: Updated existing tab:', action.payload.id, 'with metadata:', action.payload.metadata)
      } else {
        // Add new tab
        state.tabs.openTabs.push(action.payload)
        console.log('Redux: Added new tab:', action.payload.id, 'with metadata:', action.payload.metadata)
      }
      state.tabs.activeTabId = action.payload.id
      
      // Auto-hide panels if opening research tab
      if (action.payload.type === 'research') {
        state.layout.sidebarCollapsed = true
        state.layout.activityPanelCollapsed = true
        state.layout.bottomPanelCollapsed = true
      }
    },
    closeTab(state, action: PayloadAction<string>) {
      const tabId = action.payload
      
      // Don't close home tab
      if (tabId === 'home') return
      
      const tabIndex = state.tabs.openTabs.findIndex(t => t.id === tabId)
      if (tabIndex > -1) {
        state.tabs.openTabs.splice(tabIndex, 1)
        
        // If closing active tab, switch to adjacent tab
        if (state.tabs.activeTabId === tabId && state.tabs.openTabs.length > 0) {
          const newActiveIndex = Math.min(tabIndex, state.tabs.openTabs.length - 1)
          state.tabs.activeTabId = state.tabs.openTabs[newActiveIndex].id
        }
      }
    },
    
    // Search actions
    setSearchQuery(state, action: PayloadAction<string>) {
      state.search.query = action.payload
    },
    setSearchFilters(state, action: PayloadAction<UIState['search']['filters']>) {
      state.search.filters = action.payload
    },
    
    // Research actions
    addResearchMessage(state, action: PayloadAction<ResearchMessage>) {
      state.research.messages.push(action.payload)
    },
    setResearchSearching(state, action: PayloadAction<boolean>) {
      state.research.isSearching = action.payload
    },
    setResearchInput(state, action: PayloadAction<string>) {
      state.research.inputValue = action.payload
    },
    markResearchMessageSaved(state, action: PayloadAction<string>) {
      const message = state.research.messages.find(m => m.id === action.payload)
      if (message) {
        message.saved = true
      }
    },
    clearResearchMessages(state) {
      state.research.messages = [state.research.messages[0]] // Keep initial greeting
    },
    
    // Activity log actions
    addActivityLog(state, action: PayloadAction<string>) {
      const timestamp = new Date().toLocaleTimeString()
      state.activityLog.push(`[${timestamp}] ${action.payload}`)
    },
    clearActivityLog(state) {
      state.activityLog = []
    },
    
    // Form actions
    setShowCreateCaseForm(state, action: PayloadAction<boolean>) {
      state.forms.showCreateCaseForm = action.payload
    },
    
    // Toast actions
    showToast(state, action: PayloadAction<{ message: string; type?: 'success' | 'error' | 'info' }>) {
      state.toast.show = true
      state.toast.message = action.payload.message
      state.toast.type = action.payload.type || 'success'
    },
    hideToast(state) {
      state.toast.show = false
    },
    
    // Batch actions for common workflows
    openResearchTab(state) {
      const researchTab: Tab = {
        id: 'research',
        label: 'Research Assistant',
        type: 'research',
        closable: true,
        iconName: 'Microscope'
      }
      
      const existingTab = state.tabs.openTabs.find(t => t.id === 'research')
      if (!existingTab) {
        state.tabs.openTabs.push(researchTab)
      }
      
      state.tabs.activeTabId = 'research'
      
      // Hide all panels for research mode
      state.layout.sidebarCollapsed = true
      state.layout.activityPanelCollapsed = true
      state.layout.bottomPanelCollapsed = true
    },
    
    saveResearchToNotes(state, _action: PayloadAction<string>) {
      // This will be handled by the notes slice when implemented
      // For now, just show the notes panel and toast
      state.layout.bottomPanelCollapsed = false
      state.layout.bottomPanelTab = 'notes'
      state.toast.show = true
      state.toast.message = 'Research finding saved to notes!'
      state.toast.type = 'success'
    }
  }
})

// Export actions
export const {
  setSidebarCollapsed,
  toggleSidebar,
  setRightSidebarCollapsed,
  toggleRightSidebar,
  setActivityPanelCollapsed,
  toggleActivityPanel,
  setBottomPanelCollapsed,
  toggleBottomPanel,
  setBottomPanelTab,
  toggleSidebarItem,
  setResearchMode,
  setActiveTab,
  openTab,
  closeTab,
  setSearchQuery,
  setSearchFilters,
  addResearchMessage,
  setResearchSearching,
  setResearchInput,
  markResearchMessageSaved,
  clearResearchMessages,
  addActivityLog,
  clearActivityLog,
  setShowCreateCaseForm,
  showToast,
  hideToast,
  openResearchTab,
  saveResearchToNotes
} = uiSlice.actions

// Export reducer
export default uiSlice.reducer
